import { useSnackbarStore } from '@components/shared/Snackbar/store';
import { Backdrop, Button, CircularProgress } from '@mui/material';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import { RoleEnum } from '@utils/util.types';
import { useFormik } from 'formik';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { completeInvoiceAction, createInvoice } from 'services/monitoring/monitoringService';
import { useMonitoringStore } from 'services/monitoring/monitoringStore';
import { useUsersStore } from 'services/users';
import { PaymentFormValues } from 'types/monitoring';
import * as Yup from 'yup';
import { Company } from './components/forms/Company';
import { Individual } from './components/forms/Individual';
import Invoice from './components/messages/Invoice';

const backdropStyle = {
	color: '#fff',
	zIndex: (theme: any) => theme.zIndex.drawer + 1 + 1000,
	display: 'flex',
	flexDirection: 'column',
	gap: 2,
};

const validationSchemaCompany = Yup.object({
	company_name: Yup.string().required('Company Name is required'),
	company_id_number: Yup.string().required('Company Id Number is required'),
	representative_first_name: Yup.string().required('Representative\'s First Name is required'),
	representative_last_name: Yup.string().required('Representative\'s Last Name is required'),
	// Email validation removed - will be taken from JWT token
	mobile_number: Yup.string().required('Mobile number is required'),
});

const validationSchemaIndividual = Yup.object({
	first_name: Yup.string().required('First Name is required'),
	last_name: Yup.string().required('Last Name is required'),
	id_number: Yup.string().required('ID Number is required'),
	// Email validation removed - will be taken from JWT token
	mobile_number: Yup.string().required('Mobile number is required'),
});

const initialValuesIndividual = {
	first_name: '',
	last_name: '',
	id_number: '',
	mobile_number: '',
};

const initialValuesCompany = {
	company_name: '',
	company_id_number: '',
	representative_first_name: '',
	representative_last_name: '',
	mobile_number: '',
};

export const InvoiceStep = ({ setSuccess }: any) => {
	const { t } = useTranslation();
	const [isLoading, setIsLoading] = useState(false);
	const [userType, setUserType] = useState('individual');
	const { initialStepData } = useMonitoringStore();
	const [showSuccess, setShowSuccess] = useState(false);
	const showSnackbar = useSnackbarStore((state) => state.showSnackbar);
	const { user } = useUsersStore();

	const handleCompleteInvoiceAction = async (invoice_id: string) => {
		try {
			await completeInvoiceAction(initialStepData.id, invoice_id);
		} catch (err) {
			console.error('Invoice completion failed', err);
		}
	};

	const formik = useFormik<PaymentFormValues>({
		initialValues:
			userType === RoleEnum.Individual ? initialValuesIndividual : initialValuesCompany,
		validationSchema:
			userType === RoleEnum.Individual ? validationSchemaIndividual : validationSchemaCompany,
		onSubmit: async (values) => {
			setIsLoading(true);

			try {
				let response: any;

				// Get email from user object
				const email = user?.email || '';
				
				if (!email) {
					showSnackbar('Error', t('emailNotFound'), 'error');
					setIsLoading(false);
					return;
				}

				if (userType === RoleEnum.Company) {
					response = await createInvoice({
						request_id: initialStepData.id,
						is_company: true,
						company_name: values.company_name || '',
						company_id_number: values.company_id_number || '',
						representative_first_name: values.representative_first_name || '',
						representative_last_name: values.representative_last_name || '',
						email: email, // Use email from JWT token
						mobile_number: values.mobile_number || '',
					});
				} else {
					response = await createInvoice({
						request_id: initialStepData.id,
						is_company: false,
						first_name: values.first_name || '',
						last_name: values.last_name || '',
						id_number: values.id_number || '',
						email: email, // Use email from JWT token
						mobile_number: values.mobile_number || '',
					});
				}

				handleCompleteInvoiceAction(response?.data.id);
				setShowSuccess(true);
				setSuccess(true);
				setIsLoading(false);
				
				// Clear form data and IDs only when invoice is actually processed
				localStorage.removeItem('monitoringPackagesFormData');
				localStorage.removeItem('initialStepDataId');
				console.log('Invoice processed, cleared all form data');
				
				resetForm();
			} catch (err) {
				showSnackbar('Error', t('somethingWentWrong'), 'error');
				setIsLoading(false);
			}
		},
	});

	const { errors, touched, handleSubmit, getFieldProps, isValid, dirty, resetForm } = formik;

	const handleSubmitForm = (event: React.MouseEvent<HTMLButtonElement>) => {
		event.preventDefault();
		handleSubmit();
	};

	return (
		<form className="flex flex-col xs:mt-0 h-full gap-[20px] p-[20px] relative justify-between">
			<div className="flex items-center h-full justify-center sm:mt-0 mt-[20px]">
				{showSuccess ? (
					<Invoice email={user?.email} />
				) : (
					<div className="flex flex-wrap flex-col max-w-[500px] w-full gap-[20px]">
						<h1 className="text-dark text-center font-bold text-xl leading-normal mt-[20px]">
							{t('invoice')}
						</h1>
						<ToggleButtonGroup
							color="primary"
							value={userType}
							className="flex items-center justify-center"
							exclusive
							onChange={(_, newUserType) => setUserType(newUserType || userType)}
							aria-label="Platform"
						>
							<ToggleButton value="individual">{t('individual')}</ToggleButton>
							<ToggleButton value="company">{t('company')}</ToggleButton>
						</ToggleButtonGroup>

						{userType === RoleEnum.Individual ? (
							<Individual getFieldProps={getFieldProps} touched={touched} errors={errors} />
						) : (
							<Company getFieldProps={getFieldProps} touched={touched} errors={errors} />
						)}

						<Button
							className="w-full"
							onClick={handleSubmitForm}
							loading={isLoading}
							disabled={!isValid || !dirty}
							variant="contained"
						>
							{t('findSpecialistPage.monitoring.requestInvoice')}
						</Button>

						<Backdrop sx={backdropStyle} open={isLoading}>
							<CircularProgress color="primary" />
						</Backdrop>
					</div>
				)}
			</div>
		</form>
	);
};
